import { Injectable, inject } from '@angular/core';
import { Subject, Observable } from 'rxjs';
import { first } from 'rxjs/operators';
import { AppDatabaseService } from './database/app-database.service';

@Injectable({
  providedIn: 'root',
})
export class AppProperties {
  private readonly COLLECTION_NAME = 'properties';
  private readonly database = inject(AppDatabaseService);

  private readonly currValues: { [key: string]: any } = {};
  private readonly $subjectsByKey: Array<SubjectByKey> = [];
  $subsByKey: Array<any> = [];
  keysListened: Array<string> = [];

  public listen(key: AppPropertiesType, auctionId: string) {
    const keyListener = `${key}-${auctionId}`;
    const isListened =
      this.keysListened.findIndex((k) => k === keyListener) >= 0;
    if (!isListened) {
      const subs = new Observable<any>((observer) => {
        const unsubscribe = this.database.readLive(`${auctionId}/sections/${this.COLLECTION_NAME}/${key}`, (doc) => {
          const newValues = doc.data();
          observer.next(newValues);
        });
        return unsubscribe;
      }).subscribe((newValues: any) => {
        const valueKeys = Object.keys(newValues);
        let hasChanges = false;

        for (const valueKey of valueKeys) {
          const subjectEntry = this.getNotifier(keyListener, valueKey);
          if (subjectEntry) {
            const isPropStale = this.isStale(
              keyListener,
              valueKey,
              newValues[valueKey]
            );
            if (isPropStale) {
              subjectEntry.subject.next(newValues);
              hasChanges = true;
            }
          }
        }

        if (hasChanges) {
          const documentSubject = this.getNotifier(keyListener, null);
          documentSubject?.subject.next(newValues);
        }

        this.currValues[keyListener] = { ...newValues };
      });

      this.keysListened.push(keyListener);
      this.$subsByKey.push({ keyListener, subs });
    }
  }

  public getNotification(
    auctionId: string,
    key: AppPropertiesType,
    name: AppPropertiesNames | null = null
  ) {
    const keyNotifier = `${key}-${auctionId}`;
    const subjectEntry = this.getNotifier(keyNotifier, name);
    if (!subjectEntry) {
      const newSubject = {
        key: keyNotifier,
        name,
        subject: new Subject<any>(),
      };
      this.$subjectsByKey.push(newSubject);
      return newSubject.subject.asObservable();
    }

    return subjectEntry.subject.asObservable();
  }

  public destroy(auctionId: string, key: AppPropertiesType): Promise<void> {
    const keyListened = `${key}-${auctionId}`;

    return new Promise((res) => {
      if (this.keysListened && this.keysListened.length > 0) {
        const listenedIndex = this.keysListened.findIndex(
          (k) => k === keyListened
        );
        if (listenedIndex >= 0) {
          this.keysListened.splice(listenedIndex, 1);
        }
      }

      if (this.$subjectsByKey && this.$subjectsByKey.length > 0) {
        const subjects = this.$subjectsByKey.filter(
          (subject) => subject.key === keyListened
        );

        for (const sub of subjects) {
          const subjectIndex = this.$subjectsByKey.findIndex(
            (subject) =>
              subject.key === keyListened && subject.name === sub.name
          );

          if (subjectIndex >= 0) {
            const removedSubject = this.$subjectsByKey.splice(subjectIndex, 1);
            removedSubject[0].subject.complete();
          }
        }
      }

      const subIndex = this.$subsByKey.findIndex((s) => s.key === keyListened);
      if (subIndex >= 0) {
        const removedSub = this.$subsByKey.splice(subIndex, 1);
        removedSub[0].subs.unsubscribe();
      }

      res();
    });
  }

  public getOnce(
    key: AppPropertiesType,
    name: AppPropertiesNames | null = null,
    auctionId: string
  ) {
    return new Observable<any>((observer) => {
      const unsubscribe = this.database.readLive(`${auctionId}/sections/${this.COLLECTION_NAME}/${key}`, (doc) => {
          const values = doc.data();
          observer.next(name ? values?.[name] : values);
          observer.complete();
        }
      );
      return unsubscribe;
    }).pipe(first());
  }

  private isStale(key: string, propName: string, propValue: any) {
    const isKeyDefined = this.currValues[key] !== undefined;
    const isPropNameStale =
      isKeyDefined &&
      (this.currValues[key][propName] === undefined ||
        this.currValues[key][propName] !== propValue);
    return isPropNameStale;
  }

  private getNotifier(key: string, name: any) {
    return this.$subjectsByKey.find(
      (s) => s.key === key && (s.name === name || !name)
    );
  }
}

export interface SubjectByKey {
  key: string;
  name: AppPropertiesNames | null;
  subject: Subject<any>;
}

export enum AppPropertiesType {
  AUDIOPROPS = 'audioService',
  MONITORING = 'monitoring',
}

export enum AppPropertiesNames {
  CHANNELNAME = 'channelName',
  PROVIDERNAME = 'providerName',
  IS_STREAMING = 'isStreaming',
  AUDIO_ID = 'audioId',

  MONITORING_ENABLED = 'enabled',
  MONITORING_PROVIDER = 'provider',
  MONITORING_PROJECT_KEY = 'projectKey',
  MONITORING_WIDGET_SUPPORT = 'widgetSupport',
}
